/**
 * @file ContentItem.tsx
 * @description Component for displaying a single content item.
 */

import React, { useState, useRef, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

import { GeneratedContentDetails } from "../../types";
import { downloadContent } from "../../services/contentService";
import { publishContent } from "../../services/wordpressService";
import log from "../../config/logging";

import PencilIcon from "./icons/PencilIcon";
import DownloadButton from "./DownloadButton";
import WordPressPublishButton from "./WordPressPublishButton";

/**
 * Calculated height for the dropdown menu to ensure it's fully visible.
 * Based on: menu padding (16px) + 2 menu items (~80px) + shadow/margins (~4px) + safety margin.
 */
const DROPDOWN_HEIGHT = 150;

interface ContentItemProps {
  contentDetail: GeneratedContentDetails;
  onDownloadStatusChange: (id: string, downloaded: boolean) => void;
}

const ContentItem: React.FC<ContentItemProps> = ({
  contentDetail,
  onDownloadStatusChange,
}) => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const collapseRef = useRef<HTMLDivElement>(null);
  const collapseContentRef = useRef<HTMLDivElement>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isPublishingToWordPress, setIsPublishingToWordPress] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const handleShow = () => {
    navigate(`/contenuto/${contentDetail.id}`);
  };

  const handleDownload = async (format: "docx" | "wxr") => {
    try {
      setIsDownloading(true);
      await downloadContent(contentDetail.id, contentDetail.title, format);
      log.debug("Content downloaded successfully", {
        id: contentDetail.id,
        format,
      });
      toast.success("Contenuto scaricato");
      onDownloadStatusChange(contentDetail.id, true);
    } catch (error) {
      log.error("Error downloading content", { error, format });
      toast.error("Errore durante il download del contenuto");
    } finally {
      setIsDownloading(false);
    }
  };

  const handlePublishToWordPress = async () => {
    try {
      setIsPublishingToWordPress(true);
      const result = await publishContent(contentDetail.id);
      toast.success(
        `Contenuto pubblicato su WordPress! ${result.link ? `Visualizza: ${result.link}` : ""}`
      );
      log.info("Content published to WordPress successfully", {
        contentId: contentDetail.id,
        postId: result.postId,
      });
    } catch (error) {
      log.error("Error publishing content to WordPress", { error });
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Errore durante la pubblicazione su WordPress";
      toast.error(errorMessage);
    } finally {
      setIsPublishingToWordPress(false);
    }
  };

  const toggleCollapse = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      if (isOpen) {
        collapseRef.current?.blur();
      }
      setIsOpen(!isOpen);
    },
    [isOpen]
  );

  useEffect(() => {
    if (!collapseContentRef.current) return;

    if (isDropdownOpen) {
      collapseContentRef.current.style.paddingBottom = `${DROPDOWN_HEIGHT}px`;
      collapseContentRef.current.style.maxHeight = "none";
    } else {
      collapseContentRef.current.style.paddingBottom = "";
      collapseContentRef.current.style.maxHeight = "";
    }
  }, [isDropdownOpen]);

  return (
    <div
      ref={collapseRef}
      tabIndex={0}
      className={`flex flex-col w-full gap-4 collapse collapse-arrow ${
        isOpen ? "collapse-open" : ""
      }`}
    >
      <div className="flex flex-row justify-between">
        <div className="flex justify-start gap-8">
          <PencilIcon className="self-center text-primary w-11 h-11" />
          <div className="flex flex-col items-start gap-6">
            <div>
              <h2 className="!text-2xl !font-light text-base-content !mb-1">
                {contentDetail.status === "completed"
                  ? contentDetail.title
                  : "Contenuto da generare"}
              </h2>
              <p className="text-base text-gray">
                {new Date(contentDetail.updatedAt).toLocaleDateString()}
              </p>
            </div>
            <button
              onClick={handleShow}
              className="text-lg rounded-full btn btn-outline btn-primary border-gray-line"
            >
              Visualizza
            </button>
          </div>
        </div>
        <div className="w-0 h-0 collapse-title" onClick={toggleCollapse}></div>
      </div>
      <div ref={collapseContentRef} className="collapse-content">
        <div className="divider"></div>
        <div className="flex flex-row items-center justify-between pl-8 ml-11">
          <div className="flex flex-row content-center gap-2">
            {contentDetail.downloaded && (
              <>
                <CheckCircleIcon className="w-6 h-6 text-primary" />
                <div className="text-gray">Scaricato</div>
              </>
            )}
          </div>
          <div className="flex gap-2">
            <DownloadButton
              onDownload={handleDownload}
              isLoading={isDownloading}
              variant="success"
              onOpenChange={setIsDropdownOpen}
            />
            {contentDetail.status === "completed" && (
              <WordPressPublishButton
                onPublish={handlePublishToWordPress}
                isLoading={isPublishingToWordPress}
                disabled={isDownloading}
                variant="success"
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContentItem;
